import { useState, useMemo, useEffect } from 'react';
import {
  Loader2,
  ZoomIn,
  ZoomOut,
  Download,
  CheckCircle,
  MessageSquare,
  PenTool,
  FileText,
  Euro,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import { ISavV2 } from '@/models/savV2';
import type { ISavV2Decision } from '@/models/savV2Decision';
import { savApi } from '../../api/savApi';
import { axiosInstance } from '@/routes/outil-pro/api/axiosConfig';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';
import { formatDate, formatDateTime } from '@/utils/dateUtils';
import { getVehiclePurchaseDate } from '@/utils/contractHelpers';
import { Document, Page } from '@/Components/shared/react-pdf-lazy';

interface DecisionStepProps {
  sav: ISavV2;
  decision: ISavV2Decision;
  showValidateAndSignButton: boolean;
  showSignatureButton: boolean;
  showContactButton: boolean;
  isValidating: boolean;
  onAccessDecisionClick: () => void;
  onContactClick: () => void;
  completedAt?: string | null;
  allDecisions?: ISavV2Decision[];
}

const DECISION_TYPE_CONFIG = {
  ACCORD: { label: 'Accord total', bgClass: 'tw-bg-green-100', textClass: 'tw-text-green-800' },
  PARTIAL: { label: 'Accord partiel', bgClass: 'tw-bg-orange-100', textClass: 'tw-text-orange-800' },
  REFUSAL: { label: 'Refus', bgClass: 'tw-bg-red-100', textClass: 'tw-text-red-800' },
} as const;

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon',
  SENT_TO_GARANT: 'Envoyée au garant',
  ACCEPTED: 'Acceptée',
  REFUSED: 'Refusée',
  CANCELLED: 'Annulée',
  DELETED: 'Supprimée',
  GARANT_VALIDATED: 'Validée par le garant',
  GARANT_REFUSED: 'Refusée par le garant',
  PENDING_GARANT_SIGNATURE: 'En attente signature garant',
  PENDING_BENEFICIARY_SIGNATURE: 'En attente signature bénéficiaire',
  SIGNED: 'Signée',
};

export function DecisionStep({
  sav,
  decision,
  showValidateAndSignButton,
  showSignatureButton,
  showContactButton,
  isValidating,
  onAccessDecisionClick,
  onContactClick,
  completedAt,
  allDecisions = [],
}: DecisionStepProps) {
  // Décision courante en premier, puis les autres par date décroissante
  const sortedDecisions = useMemo(() => {
    const filtered = allDecisions.filter(d => d.status !== 'DRAFT');
    if (filtered.length <= 1) return [];
    const current = filtered.filter(d => d.id === decision.id);
    const others = filtered.filter(d => d.id !== decision.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return [...current, ...others];
  }, [allDecisions, decision.id]);

  const hasMultipleDecisions = sortedDecisions.length > 1;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const activeDecision = hasMultipleDecisions ? sortedDecisions[selectedIndex] : decision;
  const isViewingCurrent = !hasMultipleDecisions || activeDecision.id === decision.id;

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);

  const pdfUrl = useMemo(() => {
    if (!pdfBlob) return null;
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]);

  // Fetch PDF blob — recharger quand la décision active change
  useEffect(() => {
    if (!activeDecision.pdfDocumentId) {
      setPdfBlob(null);
      setPdfLoading(false);
      setPdfError(false);
      return;
    }

    let cancelled = false;
    setPdfLoading(true);
    setPdfError(false);
    setPdfBlob(null);
    setNumPages(0);

    (async () => {
      try {
        const token = await savApi.getDocumentDownloadToken(sav.id, activeDecision.pdfDocumentId!);
        const response = await axiosInstance.get(
          `/documents/${activeDecision.pdfDocumentId}/download?downloadType=download&documentDownloadToken=${token}`,
          { responseType: 'blob' },
        );
        if (!cancelled) {
          setPdfBlob(new Blob([response.data], { type: 'application/pdf' }));
        }
      } catch {
        if (!cancelled) setPdfError(true);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sav.id, activeDecision.pdfDocumentId, activeDecision.id]);

  // Revoke blob URL on cleanup
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `bon-decision-${sav.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleContactClick = () => {
    onContactClick();
  };

  const decisionData = activeDecision.decisionData;
  const typeConfig = activeDecision.decisionType ? DECISION_TYPE_CONFIG[activeDecision.decisionType] : null;
  // Date d'achat du véhicule (= souscription du contrat) : demandée par le
  // garant au moment de signer le bon de décision.
  const vehiclePurchaseDate = getVehiclePurchaseDate(sav.contract);

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-border-b tw-border-gray-200">
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-m-0">
          <CheckCircle className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          Décision
          {completedAt && isViewingCurrent && (
            <span className="tw-ml-auto tw-text-xs tw-font-normal tw-normal-case tw-text-gray-500">
              Terminée le {new Date(completedAt).toLocaleDateString('fr-FR')} à {new Date(completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </h3>
        {/* Onglets pour switcher entre les décisions */}
        {hasMultipleDecisions && (
          <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-px-4 tw-pb-3">
            {sortedDecisions.map((d, i) => {
              const isActive = i === selectedIndex;
              const isCurrent = d.id === decision.id;
              const dTypeConfig = d.decisionType ? DECISION_TYPE_CONFIG[d.decisionType] : null;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSelectedIndex(i)}
                  className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-border tw-cursor-pointer tw-transition-colors tw-bg-transparent ${
                    isActive
                      ? 'tw-border-affinicar-primary tw-text-affinicar-primary tw-font-medium'
                      : 'tw-border-gray-200 tw-text-gray-500 hover:tw-border-gray-400'
                  }`}
                >
                  {dTypeConfig && (
                    <span className={`tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium ${dTypeConfig.bgClass} ${dTypeConfig.textClass}`}>
                      {dTypeConfig.label}
                    </span>
                  )}
                  <span>{STATUS_LABELS[d.status] || d.status}</span>
                  <span className="tw-text-gray-400">—</span>
                  <span>{formatDateTime(d.createdAt)}</span>
                  {isCurrent && (
                    <span className="tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-affinicar-primary/10 tw-text-affinicar-primary">
                      Actuelle
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className={`tw-p-4 sm:tw-p-6 tw-space-y-6 ${!isViewingCurrent ? 'tw-bg-orange-50/30' : ''}`}>
      {/* 0. Date d'achat du véhicule — info attendue par le garant avant de
          signer le bon de décision */}
      {vehiclePurchaseDate && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2">
          <Calendar className="tw-w-4 tw-h-4 tw-text-gray-400 tw-flex-shrink-0" />
          <span className="tw-text-xs tw-text-gray-500 tw-uppercase">Date d'achat du véhicule</span>
          <span className="tw-text-sm tw-font-medium tw-text-gray-900">{formatDate(vehiclePurchaseDate)}</span>
        </div>
      )}
      {/* 1. Bon de decision PDF */}
      {activeDecision.pdfDocumentId && (
        <div>
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Bon de décision</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <button
                onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                disabled={scale <= 0.5}
                className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50"
                title="Zoom arriere"
              >
                <ZoomOut className="tw-w-4 tw-h-4" />
              </button>
              <span className="tw-text-xs tw-text-gray-600 tw-min-w-[45px] tw-text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => setScale((s) => Math.min(2.0, s + 0.25))}
                disabled={scale >= 2.0}
                className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors disabled:tw-opacity-50"
                title="Zoom avant"
              >
                <ZoomIn className="tw-w-4 tw-h-4" />
              </button>
              {pdfUrl && (
                <button
                  onClick={handleDownload}
                  className="tw-p-1.5 tw-text-gray-600 hover:tw-bg-gray-200 tw-rounded tw-transition-colors"
                  title="Telecharger le PDF"
                >
                  <Download className="tw-w-4 tw-h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="tw-max-h-[400px] sm:tw-max-h-[500px] tw-overflow-y-auto tw-overflow-x-auto tw-bg-gray-100 tw-p-2 sm:tw-p-4">
            {pdfLoading && (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary tw-mb-3" />
                <p className="tw-text-sm tw-text-gray-500">Chargement du bon de decision...</p>
              </div>
            )}

            {pdfError && (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                <p className="tw-text-sm tw-text-red-600 tw-mb-1">Impossible de charger le PDF.</p>
              </div>
            )}

            {pdfUrl && (
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  loading={
                    <div className="tw-text-center tw-py-8">
                      <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-affinicar-primary tw-mx-auto" />
                    </div>
                  }
                  error={
                    <div className="tw-text-center tw-py-8 tw-text-red-600">
                      <p className="tw-text-sm">Erreur lors du rendu du PDF</p>
                    </div>
                  }
                >
                  {Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      className="tw-shadow-lg tw-mb-2"
                    />
                  ))}
                </Document>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2. Synthese financiere */}
      {decisionData && (
        <div>
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Euro className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Synthèse financière</span>
            </div>
            {typeConfig && (
              <span
                className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${typeConfig.bgClass} ${typeConfig.textClass}`}
              >
                {typeConfig.label}
              </span>
            )}
          </div>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 sm:tw-gap-4">
            <div className="tw-bg-green-50 tw-rounded-lg tw-p-3 tw-text-center">
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Montant couvert</p>
              <p className="tw-text-lg tw-font-semibold tw-text-green-700 tw-mb-0">
                {formatPrice(decisionData.coveredAmount != null ? decisionData.coveredAmount / 100 : null)}
              </p>
            </div>
            <div className="tw-bg-red-50 tw-rounded-lg tw-p-3 tw-text-center">
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Montant non couvert</p>
              <p className="tw-text-lg tw-font-semibold tw-text-red-700 tw-mb-0">
                {formatPrice(decisionData.uncoveredAmount != null ? decisionData.uncoveredAmount / 100 : null)}
              </p>
            </div>
            <div className="tw-bg-blue-50 tw-rounded-lg tw-p-3 tw-text-center">
              <p className="tw-text-xs tw-text-gray-500 tw-mb-1">Geste commercial</p>
              <p className="tw-text-lg tw-font-semibold tw-text-blue-700 tw-mb-0">
                {formatPrice(
                  decisionData.totalCommercialAmount != null ? decisionData.totalCommercialAmount / 100 : null,
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 3. Boutons de validation — uniquement pour la décision courante */}
      {isViewingCurrent && (showValidateAndSignButton || decision) && !decision?.signatures?.garant?.signedAt && (
        <div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <CheckCircle className="tw-w-4 tw-h-4 tw-text-gray-400" />
            <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Votre décision</span>
          </div>
          <div className="tw-flex tw-flex-col tw-w-full tw-gap-3">
            {/* Accès à la page de validation dédiée (consultation + accepter/
                refuser + signature). Le gate showValidateAndSignButton inclut
                déjà la permission sav.sign : seuls les habilités le voient et
                génèrent le lien (token à la volée). */}
            {showValidateAndSignButton && (
              <button
                type="button"
                onClick={onAccessDecisionClick}
                disabled={isValidating}
                className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-3 tw-rounded-md tw-text-base tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {isValidating ? (
                  <Loader2 className="tw-w-5 tw-h-5 tw-animate-spin" />
                ) : (
                  <ExternalLink className="tw-w-5 tw-h-5" />
                )}
                Accéder à la décision
              </button>
            )}
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-gap-3">
              {decision && showContactButton && (
                <button
                  type="button"
                  onClick={handleContactClick}
                  className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-transition-colors"
                >
                  <MessageSquare className="tw-w-4 tw-h-4" />
                  Vous avez une question sur la recommandation ? Contactez nous
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. Signatures — masquées pour une décision annulée/refusée où
          personne n'a signé : afficher « En attente » ×2 sur une décision
          qui ne sera jamais signée (ex. annulée par un sans suite manuel)
          laissait croire à une action à venir. Si une signature a réellement
          eu lieu avant l'annulation, on garde l'historique visible. */}
      {activeDecision.signatures &&
        activeDecision.status !== 'DELETED' &&
        (!['CANCELLED', 'GARANT_REFUSED'].includes(activeDecision.status) ||
          activeDecision.signatures.garant?.signedAt ||
          activeDecision.signatures.beneficiary?.signedAt) && (
        <div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <PenTool className="tw-w-4 tw-h-4 tw-text-gray-400" />
            <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Signatures</span>
          </div>
          <div className="tw-space-y-3">
            {/* Garant signature */}
            <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
              <div className="tw-flex tw-items-center tw-gap-3">
                <PenTool className="tw-w-4 tw-h-4 tw-text-gray-500" />
                <div>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">
                    Signature garant
                  </p>
                  {activeDecision.signatures?.garant?.name && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
                      {activeDecision.signatures.garant.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="tw-text-right">
                {activeDecision.signatures?.garant?.signedAt ? (
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <CheckCircle className="tw-w-4 tw-h-4 tw-text-green-500" />
                    <span className="tw-text-xs tw-text-green-700 tw-font-medium">
                      Signe le {formatDate(activeDecision.signatures.garant.signedAt)}
                    </span>
                  </div>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-yellow-100 tw-text-yellow-800">
                    En attente
                  </span>
                )}
              </div>
            </div>

            {/* Beneficiary signature */}
            <div className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-bg-gray-50 tw-rounded-lg">
              <div className="tw-flex tw-items-center tw-gap-3">
                <PenTool className="tw-w-4 tw-h-4 tw-text-gray-500" />
                <div>
                  <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-0">
                    Signature beneficiaire
                  </p>
                  {activeDecision.signatures?.beneficiary?.name && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0">
                      {activeDecision.signatures.beneficiary.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="tw-text-right">
                {activeDecision.signatures?.beneficiary?.signedAt ? (
                  <div className="tw-flex tw-items-center tw-gap-1.5">
                    <CheckCircle className="tw-w-4 tw-h-4 tw-text-green-500" />
                    <span className="tw-text-xs tw-text-green-700 tw-font-medium">
                      Signe le {formatDate(activeDecision.signatures.beneficiary.signedAt)}
                    </span>
                  </div>
                ) : (
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-yellow-100 tw-text-yellow-800">
                    En attente
                  </span>
                )}
              </div>
            </div>

            {/* Signature button — passe par la page de validation (flux
                serveur), PAS par le lien Yousign brut stocké : l'API y
                applique la règle « le valideur signe en son nom » (réutilise
                le lien si c'est la même personne, sinon annule et recrée la
                demande au nom du nouvel utilisateur — un lien brut
                contournerait cette logique et ferait signer sous l'identité
                de quelqu'un d'autre). */}
            {isViewingCurrent && showSignatureButton && activeDecision.signatures?.garant?.signatureLink && (
              <div className="tw-mt-3">
                <button
                  type="button"
                  onClick={onAccessDecisionClick}
                  disabled={isValidating}
                  className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                  {isValidating ? (
                    <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                  ) : (
                    <PenTool className="tw-w-4 tw-h-4" />
                  )}
                  Signer le bon de décision
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
